import { buildCircuitBreakerViewModel, getLossExposure } from './circuitBreakerModel.js'

export const finiteValue = (value) => {
  if (value === null || value === undefined || (typeof value === 'string' && !value.trim()) || typeof value === 'boolean')
    return null
  const number = Number(value)
  return Number.isFinite(number) ? number : null
}

export function timestampMs(value) {
  if (value === null || value === undefined || value === '') return null
  const numeric = Number(value)
  const parsed = Number.isFinite(numeric)
    ? numeric < 1e12
      ? numeric * 1000
      : numeric
    : Date.parse(value)
  return Number.isFinite(parsed) ? parsed : null
}

const accountKey = (broker, id) => `${String(broker).toLowerCase()}:${id}`
export const ACCOUNT_STALE_MS = 10 * 60 * 1000

export function buildAccountRows(pnl, breakers, now = Date.now()) {
  const protection = new Map(
    (breakers?.accounts || []).map((item) => [accountKey(item.broker, item.account_id), item]),
  )
  return (Array.isArray(pnl) ? pnl : [])
    .map((account) => {
      const key = accountKey(account.broker, account.account_login)
      const breaker = protection.get(key)
      const view = breaker ? buildCircuitBreakerViewModel(breaker) : null
      const updatedAt = timestampMs(account.timestamp)
      const stale =
        updatedAt === null || now - updatedAt > ACCOUNT_STALE_MS || updatedAt > now + 60000
      const blocked =
        breaker?.should_block_new_orders === true ||
        breaker?.lock_for_day === true ||
        breaker?.status === 'ACTIVE'
      const attention =
        blocked ||
        breaker?.should_close_account === true ||
        breaker?.bad_day_armed === true ||
        breaker?.risk_state === 'BAD_DAY_ARMED' ||
        Boolean(breaker?.close_error)
      const dataIssue = !breaker || breaker.data_state !== 'valid' || stale
      return {
        key,
        broker: String(account.broker || 'Unknown'),
        account: String(account.account_login || 'Unknown'),
        currency: String(account.currency || 'UNKNOWN').toUpperCase(),
        balance: finiteValue(account.balance),
        realized: finiteValue(account.summary?.tot_realized_pnl),
        floating: finiteValue(account.summary?.tot_floating_pnl),
        total: finiteValue(account.summary?.tot_pnl),
        positions: finiteValue(account.summary?.tot_positions),
        updatedAt,
        stale,
        breaker,
        protection:
          breaker?.data_state === 'valid' && breaker?.should_close_account === true
            ? { label: 'CLOSE REQUESTED', color: 'danger' }
            : breaker?.data_state === 'valid' && breaker?.should_block_new_orders === true
              ? { label: 'ORDERS BLOCKED', color: 'danger' }
              : view?.overall || { label: 'NOT REPORTED', color: 'secondary' },
        exposure: breaker ? getLossExposure(breaker.total_pnl, breaker.hard_loss_threshold) : null,
        blocked,
        attention,
        dataIssue,
        priority: blocked ? 0 : attention ? 1 : dataIssue ? 2 : 3,
      }
    })
    .sort(
      (a, b) =>
        a.priority - b.priority ||
        (b.exposure ?? -1) - (a.exposure ?? -1) ||
        a.key.localeCompare(b.key),
    )
}

export function currencyTotals(rows) {
  const groups = new Map()
  for (const row of rows) {
    if (!groups.has(row.currency))
      groups.set(row.currency, {
        currency: row.currency,
        accounts: 0,
        stale: 0,
        balance: null,
        realized: null,
        floating: null,
        total: null,
        incomplete: false,
      })
    const group = groups.get(row.currency)
    group.accounts++
    if (row.stale) group.stale++
    for (const metric of ['balance', 'realized', 'floating', 'total']) {
      if (row[metric] === null) group.incomplete = true
      else group[metric] = (group[metric] ?? 0) + row[metric]
    }
  }
  return [...groups.values()].sort((a, b) => a.currency.localeCompare(b.currency))
}

export function heartbeatSummary(payload, now = Date.now()) {
  if (!payload?.statuses || typeof payload.statuses !== 'object') return null
  const threshold = (finiteValue(payload.metadata?.thresholdMinutes) ?? 10) * 60000
  const counts = { total: 0, online: 0, stale: 0, offline: 0 }
  for (const value of Object.values(payload.statuses)) {
    counts.total++
    const stamp = timestampMs(
      value && typeof value === 'object'
        ? (value.heartbeatSeconds ??
            value.heartbeatTimestamp ??
            value.lastHeartbeatSeconds ??
            value.lastHeartbeatTimestamp ??
            value.timestamp)
        : value,
    )
    if (stamp === null || stamp <= 0) counts.offline++
    else if (now - stamp > threshold || stamp > now + 60000) counts.stale++
    else counts.online++
  }
  return counts
}
